import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { getInvoices, deleteInvoice } from '../api'
import { useAuth } from '../context/AuthContext'
import ProcessingOverlay from '../components/ui/ProcessingOverlay'

const PAYMENT_LABEL: Record<string, string> = {
  CASH: '💵 Cash',
  BANK_TRANSFER: '🏦 Transfer',
  CREDIT: '📋 Credit',
}

const toLocalDate = (d: Date) => {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}
const today = () => toLocalDate(new Date())
const daysAgo = (n: number) => {
  const d = new Date()
  d.setDate(d.getDate() - n)
  return toLocalDate(d)
}

type SortKey = 'invoice_number' | 'invoice_date' | 'customer_name' | 'payment_type' | 'grand_total'
type SortDir = 'asc' | 'desc'

const STORAGE_KEY = 'invoiceListState'

function getSavedState() {
  try {
    const s = sessionStorage.getItem(STORAGE_KEY)
    return s ? JSON.parse(s) : null
  } catch { return null }
}

export default function InvoiceList() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const saved = getSavedState()
  const [invoices, setInvoices] = useState<any[]>(saved?.invoices || [])
  const [totalCount, setTotalCount] = useState(saved?.totalCount || 0)
  const [page, setPage] = useState(saved?.page || 1)
  const pageSize = 50
  const [search, setSearch] = useState(saved?.search || '')
  const [fromDate, setFromDate] = useState(saved?.fromDate || daysAgo(2))
  const [toDate, setToDate] = useState(saved?.toDate || today())
  const [customerFilter, setCustomerFilter] = useState(saved?.customerFilter || '')
  const [departmentFilter, setDepartmentFilter] = useState(saved?.departmentFilter || '')
  const [amountFilter, setAmountFilter] = useState(saved?.amountFilter || '')
  const [loading, setLoading] = useState(!saved)
  const [processing, setProcessing] = useState('')
  const [sortKey, setSortKey] = useState<SortKey>(saved?.sortKey || 'invoice_date')
  const [sortDir, setSortDir] = useState<SortDir>(saved?.sortDir || 'desc')

  const load = (p = page) => {
    setLoading(true)
    const params: any = { page: p, page_size: pageSize }
    if (search.trim()) params.search = search.trim()
    if (customerFilter.trim()) params.customer = customerFilter.trim()
    if (departmentFilter) params.department = departmentFilter
    if (fromDate) params.from_date = fromDate
    if (toDate) params.to_date = toDate
    if (amountFilter) params.amount = amountFilter
    getInvoices(params).then((r: any) => {
      setInvoices(r.data.data)
      setTotalCount(r.data.total)
      setPage(p)
      setLoading(false)
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
        invoices: r.data.data, totalCount: r.data.total, page: p,
        search, fromDate, toDate,
        customerFilter, departmentFilter, amountFilter, sortKey, sortDir,
      }))
    })
  }

  useEffect(() => { if (!saved) load(1) }, []) // eslint-disable-line

  const handleFromDate = (val: string) => {
    setFromDate(val)
    if (val > toDate) setToDate(val)
  }

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    } else {
      setSortKey(key)
      setSortDir('asc')
    }
  }

  const sortIcon = (key: SortKey) => {
    if (sortKey !== key) return ' ↕'
    return sortDir === 'asc' ? ' ↑' : ' ↓'
  }

  const sorted = [...invoices].sort((a, b) => {
    let aVal = a[sortKey]
    let bVal = b[sortKey]
    if (sortKey === 'grand_total') {
      aVal = Number(aVal)
      bVal = Number(bVal)
    } else {
      aVal = String(aVal || '').toLowerCase()
      bVal = String(bVal || '').toLowerCase()
    }
    if (aVal < bVal) return sortDir === 'asc' ? -1 : 1
    if (aVal > bVal) return sortDir === 'asc' ? 1 : -1
    return 0
  })

  const handleDelete = async (id: number, num: string) => {
    if (!window.confirm(`Delete invoice ${num}?`)) return
    setProcessing('Deleting invoice...')
    try { await deleteInvoice(id); load(page) } finally { setProcessing('') }
  }

  return (
    <div className="page">
      {processing && <ProcessingOverlay message={processing} />}
      <div className="page-header">
        <div>
          <div className="page-title">Sales Invoices</div>
          <div className="page-subtitle">Faktur Penjualan</div>
        </div>
        <button className="btn btn-primary" onClick={() => navigate('/invoices/new')}>
          + New Invoice
        </button>
      </div>

      <div className="card">
        <div className="filter-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 2fr 2fr 1fr auto', gap: 12, marginBottom: 16, alignItems: 'end' }}>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label">From Date</label>
            <input type="date" className="form-input" value={fromDate} onChange={e => handleFromDate(e.target.value)} />
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label">To Date</label>
            <input type="date" className="form-input" value={toDate} min={fromDate} onChange={e => setToDate(e.target.value)} />
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label">Customer</label>
            <input className="form-input" placeholder="Filter by customer..." value={customerFilter} onChange={e => setCustomerFilter(e.target.value)} />
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label">Department</label>
            <select className="form-input" value={departmentFilter} onChange={e => setDepartmentFilter(e.target.value)}>
              <option value="">All</option>
              <option value="SIL">Laptop (SIL)</option>
              <option value="SIH">Mobile (SIH)</option>
            </select>
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label">Invoice Number</label>
            <input className="form-input" placeholder="Search invoice..." value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label">Amount</label>
            <input type="text" inputMode="numeric" className="form-input" placeholder="Grand total..."
              value={amountFilter ? Number(amountFilter).toLocaleString('en-US') : ''}
              onChange={e => setAmountFilter(e.target.value.replace(/[^0-9]/g, ''))} />
          </div>
          <button className="btn btn-primary" style={{ height: 38 }} onClick={() => load(1)}>🔍 Search</button>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>Loading...</div>
        ) : (
          <>
            <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th style={{ cursor: 'pointer' }} onClick={() => handleSort('invoice_number')}>Invoice No{sortIcon('invoice_number')}</th>
                  <th style={{ cursor: 'pointer' }} onClick={() => handleSort('invoice_date')}>Date{sortIcon('invoice_date')}</th>
                  <th style={{ cursor: 'pointer' }} onClick={() => handleSort('customer_name')}>Customer{sortIcon('customer_name')}</th>
                  <th style={{ cursor: 'pointer' }} onClick={() => handleSort('payment_type')}>Payment{sortIcon('payment_type')}</th>
                  <th style={{ cursor: 'pointer' }} className="right" onClick={() => handleSort('grand_total')}>Grand Total{sortIcon('grand_total')}</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sorted.length === 0 && (
                  <tr><td colSpan={6} style={{ textAlign: 'center', color: '#94a3b8', padding: 32 }}>No invoices found</td></tr>
                )}
                {sorted.map((inv: any) => (
                  <tr key={inv.id}>
                    <td><strong style={{ color: '#2563eb' }}>{inv.invoice_number}</strong></td>
                    <td style={{ color: '#64748b' }}>{inv.invoice_date ? inv.invoice_date.split('-').reverse().join('-') : ''}</td>
                    <td style={{ fontWeight: 500 }}>{inv.customer_name}</td>
                    <td><span className="badge badge-draft" style={{ fontSize: 11 }}>{PAYMENT_LABEL[inv.payment_type] || inv.payment_type}</span></td>
                    <td className="right" style={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                      {Number(inv.grand_total).toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button className="btn btn-sm btn-ghost" onClick={() => navigate(`/invoices/${inv.id}/print`)}>🖨 Print</button>
                        <button className="btn btn-sm btn-secondary" onClick={() => navigate(`/invoices/${inv.id}/edit`)}>Edit</button>
                        {(user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN') && (
                          <button className="btn btn-sm btn-danger" onClick={() => handleDelete(inv.id, inv.invoice_number)}>Delete</button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
            <div style={{ marginTop: 12, fontSize: 13, color: '#64748b', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>Showing {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, totalCount)} of {totalCount} invoices</span>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-sm btn-secondary" disabled={page <= 1} onClick={() => load(page - 1)}>← Prev</button>
                <span style={{ padding: '4px 8px' }}>Page {page} / {Math.ceil(totalCount / pageSize)}</span>
                <button className="btn btn-sm btn-secondary" disabled={page >= Math.ceil(totalCount / pageSize)} onClick={() => load(page + 1)}>Next →</button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
